import { useState, useCallback } from 'react';
import {
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  collection,
  addDoc,
  Timestamp,
  DocumentData,
  FirestoreError,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import {
  Usuario,
  Cliente,
  Unidade,
  Transacao,
  Produto,
  CategoriaProduto,
  Campanha,
  RegraCampanha,
  NivelFidelidade,
  Badge,
  OperadorCaixa,
  FirestoreDocument,
} from '../types/firestore.types';

/**
 * Tipo genérico para os dados de um documento Firestore, garantindo a presença de campos de timestamp.
 * Para criação, o ID é opcional, pois o Firestore pode gerá-lo.
 */
type FirestoreDocumentData<T extends FirestoreDocument> = Omit<T, 'id' | 'criado_em' | 'atualizado_em'> & {
  criado_em?: Timestamp;
  atualizado_em?: Timestamp;
};

/**
 * Um hook React personalizado para realizar operações de mutação (criar, atualizar, excluir)
 * em uma coleção específica do Firestore. Ele gerencia os estados de carregamento e erro,
 * proporcionando uma API limpa e robusta para interagir com o banco de dados.
 *
 * @template T O tipo de dado do documento Firestore, que deve estender `FirestoreDocument`.
 * @param {string} colecao O nome da coleção no Firestore onde as mutações serão realizadas.
 * @returns Um objeto contendo funções para criar, atualizar e excluir documentos,
 *          além dos estados de carregamento e erro.
 */
export function useFirestoreMutation<T extends FirestoreDocument>(colecao: string) {
  const [carregando, setCarregando] = useState<boolean>(false);
  const [erro, setErro] = useState<FirestoreError | null>(null);

  /**
   * Cria um novo documento na coleção especificada.
   * Adiciona automaticamente os campos `criado_em` e `atualizado_em` com o timestamp atual.
   *
   * @param {FirestoreDocumentData<T>} dados Os dados do documento a serem criados.
   * @returns {Promise<string>} Uma promessa que resolve com o ID do documento criado.
   */
  const criar = useCallback(async (dados: FirestoreDocumentData<T>): Promise<string> => {
    setCarregando(true);
    setErro(null);
    try {
      const timestampAtual = Timestamp.now();
      const dadosComTimestamps = {
        ...dados,
        criado_em: timestampAtual,
        atualizado_em: timestampAtual,
      } as DocumentData;

      const docRef = await addDoc(collection(db, colecao), dadosComTimestamps);
      return docRef.id;
    } catch (error) {
      console.error(`Erro ao criar documento na coleção '${colecao}':`, error);
      setErro(error as FirestoreError);
      throw error;
    } finally {
      setCarregando(false);
    }
  }, [colecao]);

  /**
   * Atualiza um documento existente na coleção especificada.
   * Adiciona automaticamente o campo `atualizado_em` com o timestamp atual.
   *
   * @param {string} id O ID do documento a ser atualizado.
   * @param {Partial<FirestoreDocumentData<T>>} dados Os dados parciais do documento a serem atualizados.
   * @returns {Promise<void>} Uma promessa que resolve quando o documento é atualizado.
   */
  const atualizar = useCallback(async (id: string, dados: Partial<FirestoreDocumentData<T>>): Promise<void> => {
    setCarregando(true);
    setErro(null);
    try {
      const docRef = doc(db, colecao, id);
      const dadosComTimestampAtualizado = {
        ...dados,
        atualizado_em: Timestamp.now(),
      } as DocumentData;
      await updateDoc(docRef, dadosComTimestampAtualizado);
    } catch (error) {
      console.error(`Erro ao atualizar documento '${id}' na coleção '${colecao}':`, error);
      setErro(error as FirestoreError);
      throw error;
    } finally {
      setCarregando(false);
    }
  }, [colecao]);

  /**
   * Exclui um documento da coleção especificada.
   *
   * @param {string} id O ID do documento a ser excluído.
   * @returns {Promise<void>} Uma promessa que resolve quando o documento é excluído.
   */
  const excluir = useCallback(async (id: string): Promise<void> => {
    setCarregando(true);
    setErro(null);
    try {
      const docRef = doc(db, colecao, id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error(`Erro ao excluir documento '${id}' da coleção '${colecao}':`, error);
      setErro(error as FirestoreError);
      throw error;
    } finally {
      setCarregando(false);
    }
  }, [colecao]);

  return {
    criar,
    atualizar,
    excluir,
    carregando,
    erro,
  };
}